"use client";

import React, {createContext, useContext} from "react";
import {DeleteButton} from "@/components/forms/DeleteButton";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/tremor/Dialog";

export type DeleteConfirmationDictionary = {
    title: string;
    description: string;
    confirmLabel: string;
    cancelLabel: string;
    pendingLabel: string;
};

const DeleteConfirmationContext = createContext<DeleteConfirmationDictionary | null>(null);

export function DeleteConfirmationProvider({
    dictionary,
    children,
}: {
    dictionary: DeleteConfirmationDictionary;
    children: React.ReactNode;
}) {
    return (
        <DeleteConfirmationContext.Provider value={dictionary}>
            {children}
        </DeleteConfirmationContext.Provider>
    );
}

export function DeleteConfirmation({
    open,
    pending,
    title,
    label,
    description,
    cancelLabel,
    pendingLabel,
    error,
    onOpenChange,
    onConfirm,
}: {
    open: boolean;
    pending: boolean;
    title?: string;
    label?: string;
    description?: string;
    cancelLabel?: string;
    pendingLabel?: string;
    error?: string | null;
    onOpenChange: (open: boolean) => void;
    onConfirm: () => void;
}) {
    const defaults = useContext(DeleteConfirmationContext);
    const resolvedTitle = title || label || defaults?.title;
    const resolvedDescription = description || defaults?.description;
    const resolvedConfirmLabel = label || defaults?.confirmLabel;
    const resolvedCancelLabel = cancelLabel || defaults?.cancelLabel;
    const resolvedPendingLabel = pendingLabel || defaults?.pendingLabel;

    if (
        !resolvedTitle
        || !resolvedDescription
        || !resolvedConfirmLabel
        || !resolvedCancelLabel
        || !resolvedPendingLabel
    ) {
        throw new Error(
            "DeleteConfirmation requires localized defaults or explicit labels.",
        );
    }

    return (
        <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
                if (!pending) onOpenChange(nextOpen);
            }}
        >
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{resolvedTitle}</DialogTitle>
                    <DialogDescription>{resolvedDescription}</DialogDescription>
                    {error && <p className="mt-3 text-rose-500">{error}</p>}
                </DialogHeader>
                <DialogFooter className="mt-6 gap-3 sm:space-x-0">
                    <DialogClose asChild>
                        <button
                            type="button"
                            disabled={pending}
                            className={[
                                "inline-flex items-center justify-center rounded-md border-2 border-zinc-300",
                                "px-6 py-2.5 text-center font-jost text-lg font-semibold text-zinc-700",
                                "transition-colors hover:bg-zinc-100",
                                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-zinc-300",
                                "disabled:cursor-not-allowed disabled:opacity-50",
                            ].join(" ")}
                        >
                            {resolvedCancelLabel}
                        </button>
                    </DialogClose>
                    <DeleteButton
                        type="button"
                        label={resolvedConfirmLabel}
                        pendingLabel={resolvedPendingLabel}
                        pending={pending}
                        onClick={onConfirm}
                    />
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export const DeleteConfirmationDialog = DeleteConfirmation;
